import { ForecastGridFixedFields } from '../../ForecastGridConstants';

/**
 * Determines if the current user, identified by 'userAlias', has authorization to edit the row.
 *
 * @param {any} rowData - The data of the row to check.
 * @param {string} userAlias - The alias of the current user.
 * @param {boolean} isFinanceAdminOrFinanceOwner - Flag indicating if the current user is a Finance Admin or Finance Owner.
 * @param {boolean} isBudgetLeader - Flag indicating if the current user is a Budget Leader.
 * @returns {boolean} - Returns true if the user is authorized (either the Budget Owner or an Admin), otherwise returns false.
 */
export const isUserAuthorizedToEditThisRow = (
  rowData: any,
  userAlias: string,
  isFinanceAdminOrFinanceOwner: boolean,
  isBudgetLeader: boolean
): boolean => {
  try {
    return rowData[ForecastGridFixedFields.BudgetOwner.value] === userAlias || isFinanceAdminOrFinanceOwner || isBudgetLeader;
  } catch (error: any) {
    // If rowData is not available or undefined, assume the user is not authorized.
    return false;
  }
};

/**
 * Checks if the row is marked as a new FE row.
 * @param rowData The row data to check
 * @returns True if the row is a new FE row, false otherwise
 */
export const isNewFERow = (rowData: any): boolean => {
  try {
    return rowData[ForecastGridFixedFields.IsNewFERow.value];
  } catch (error: any) {
    return false;
  }
};

/**
 * Checks if the row is marked as a new row.
 * @param rowData The row data to check
 * @returns True if the row is a new row, false otherwise
 */
export const isNewRow = (rowData: any): boolean => {
  try {
    return rowData[ForecastGridFixedFields.IsNew.value];
  } catch (error: any) {
    return false;
  }
};

// Helper function to update row data and refresh cells
export const updateRowData = (node: any, rowData: any, fieldName: string, newValue: string | null) => {
  rowData[fieldName] = newValue;
  node?.setDataValue(fieldName, newValue);
  node?.gridOptionsWrapper?.gridOptions?.api?.refreshCells({ force: true });
};

/**
 * Determines if a business segment is editable based on user permissions and row state.
 * @param data The row data
 * @param userAlias The current user's alias
 * @param isFinanceAdminOrFinanceOwner Whether the user is a finance admin or owner
 * @param isBudgetLeader Whether the user is a budget leader
 * @param isIdField Whether the field is an ID field
 * @returns True if the business segment is editable, false otherwise
 */
export const isBusinessSegmentEditable = (
  data: any,
  userAlias: string,
  isFinanceAdminOrFinanceOwner: boolean,
  isBudgetLeader: boolean,
  isIdField: boolean
): boolean => {
  const isUserOwnsRecord = isUserAuthorizedToEditThisRow(data, userAlias, isFinanceAdminOrFinanceOwner, isBudgetLeader);
  const isEditable = isNewFERow(data) || isNewRow(data) ? isUserOwnsRecord : !isIdField && isUserOwnsRecord;
  return isEditable;
};

/**
 * Determines if a corp segment is editable based on user permissions and row state.
 * @param data The row data
 * @param userAlias The current user's alias
 * @param isFinanceAdminOrFinanceOwner Whether the user is a finance admin or owner
 * @param isBudgetLeader Whether the user is a budget leader
 * @returns True if the corp segment is editable, false otherwise
 */
export const isCorpSegmentEditable = (data: any, userAlias: string, isFinanceAdminOrFinanceOwner: boolean, isBudgetLeader: boolean): boolean => {
  return (isNewFERow(data) || isNewRow(data)) && isUserAuthorizedToEditThisRow(data, userAlias, isFinanceAdminOrFinanceOwner, isBudgetLeader);
};
